import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import {
    BarChartNormalizedBar,
    classnames,
    CustomLink,
    DashboardDataCard,
    NumberDisplay,
    SimpleBarChart,
} from '@ygtechnologies/bygr-frontend-component-library';
import { BuyerContext } from '@/buyer/BuyerContext';
import React, { useContext, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getClosedChangeRequestCount, getOpenChangeRequestCount } from '@/services/buyer-dashboard';
import { loadBuyerBudget } from '@/state/buyer/budget/creators';
import { IState } from '@/state/IState';
import { BudgetGroupType, ProjectDecimalOption, ProjectFeature, ProjectFeatureMode } from '@/types';
import { useAppDispatch, useAsyncState, useBudgetGroupedByRoom } from '@/utils/hooks';
import { findCategoryName } from '@/utils/categories';
import { useBuyerRoomMap } from '@/services/rooms';
import BuyerPageTitle from '../BuyerPageTitle/BuyerPageTitle';
import theme from './BuyerDashboard.module.scss';
import BuyerDashboardDeadlines from './BuyerDashboardDeadlines';
import { BuyerCurrencyDisplay } from '../BuyerCurrencyDisplay';

type BuyerDashboardProps = {};

interface SimplifiedBudgetGroup {
    type: BudgetGroupType;
    roomTagId: number | null;
    categoryId: number | null;
    totalPrice: number;
}

const BuyerDashboard: React.FC<BuyerDashboardProps> = () => {
    const residentialUnitId = useSelector((state: IState) => state.buyer.permissions!.residentialUnitId);
    const budget = useSelector((state: IState) => state.buyer.budget);
    const projectData = useSelector((state: IState) => state.buyer.projectData!);
    const itemCategories = useSelector((state: IState) => state.global.itemCategories);
    const groupedByRoom = useBudgetGroupedByRoom();
    // Room names are only rendered when grouping by room; skip the fetch otherwise.
    const roomMap = useBuyerRoomMap(groupedByRoom ? residentialUnitId : null);
    const totalSpending = budget ? budget.totalPrice : null;

    const filteredBudgetGroups = useMemo<SimplifiedBudgetGroup[] | null>(() => {
        if (!budget) {
            return null;
        }

        return budget.groups
            .filter((x) => x.totalPrice !== null)
            .map((x) => ({
                type: x.type,
                roomTagId: x.roomTagId,
                categoryId: x.categoryId,
                totalPrice: x.totalPrice!,
            }));
    }, [budget]);

    const [openChangeRequests] = useAsyncState(
        () => getOpenChangeRequestCount(residentialUnitId),
        [residentialUnitId],
        null
    );
    const [closedChangeRequests] = useAsyncState(
        () => getClosedChangeRequestCount(residentialUnitId),
        [residentialUnitId],
        null
    );

    const getLineName = (data: any) => {
        switch (data.type) {
            case BudgetGroupType.Normal: {
                if (groupedByRoom) {
                    const room = data.roomTagId ? roomMap?.get(data.roomTagId) : null;
                    return room?.name ?? t`General`;
                }
                return (data.categoryId ? findCategoryName(itemCategories, data.categoryId) : null) ?? t`Uncategorized`;
            }
            case BudgetGroupType.ChangeRequests:
                return <Trans>Change requests</Trans>;
            case BudgetGroupType.ExtraCosts:
                return <Trans>Extra costs</Trans>;
            case BudgetGroupType.Presets:
                return <Trans>Styles</Trans>;
            case BudgetGroupType.Other:
                return <Trans>Other</Trans>;
            default:
                return <Trans>Unknown</Trans>;
        }
    };

    const dispatch = useAppDispatch();
    useEffect(() => {
        dispatch(loadBuyerBudget());
    }, [dispatch, residentialUnitId]);

    const { hasCustomBackgroundColor } = useContext(BuyerContext);

    return (
        <>
            <BuyerPageTitle leftPadding />

            <div className={theme.dashboardContainer}>
                <div className={theme.costContainer}>
                    <div className={theme.costCardContainer}>
                        <h6>
                            <Trans>Total costs spent on options</Trans>
                        </h6>
                        <DashboardDataCard
                            className={classnames(theme.cardShadowBorder, theme.costCards)}
                            loading={totalSpending === null}>
                            <div className={theme.sumContainer}>
                                <div className={classnames(theme.sumContent)}>
                                    <span>
                                        <Trans>Total</Trans>
                                    </span>
                                    <span>
                                        <BuyerCurrencyDisplay
                                            minimumFractionDigits={
                                                projectData.decimalOption === ProjectDecimalOption.Two ? 2 : 0
                                            }
                                            maximumFractionDigits={
                                                projectData.decimalOption === ProjectDecimalOption.Two ? 2 : 0
                                            }
                                            value={totalSpending!}
                                        />
                                    </span>
                                </div>
                            </div>
                        </DashboardDataCard>
                    </div>

                    <div className={theme.costCardContainer}>
                        <h6>
                            {groupedByRoom ? <Trans>Total costs per room</Trans> : <Trans>Total costs per discipline</Trans>}
                        </h6>
                        <DashboardDataCard
                            className={classnames(theme.cardShadowBorder, theme.costCards)}
                            contentAlignment="top"
                            rowSpan={3}
                            loading={!filteredBudgetGroups}>
                            <div className={theme.barContainer}>
                                <SimpleBarChart<SimplifiedBudgetGroup>
                                    emptyText={t`An overview of what you spent money on will be displayed here`}
                                    itemTitle={groupedByRoom ? t`Room` : t`Discipline`}
                                    valueTitle={t`Sum`}
                                    data={filteredBudgetGroups!}
                                    nameKey={(row) => getLineName(row)}
                                    valueKey={'totalPrice'}
                                    barComponent={BarChartNormalizedBar}
                                />
                            </div>
                        </DashboardDataCard>
                    </div>
                </div>

                <div className={theme.changeAndDeadlinesContainer}>
                    {projectData.features?.find((x) => x.feature === ProjectFeature.ChangeRequests)?.mode !==
                        ProjectFeatureMode.Hidden && (
                        <div className={classnames(theme.dashboardCards, theme.cardShadowBorder)}>
                            <h6>
                                <CustomLink to={`/change-requests`}>
                                    <Trans>Change requests</Trans>
                                </CustomLink>
                            </h6>

                            <div className={theme.changeRequestContent}>
                                <span>
                                    <Trans>In progress</Trans>:
                                </span>
                                <span>{openChangeRequests}</span>
                            </div>

                            <div className={theme.changeRequestContent}>
                                <span>
                                    <Trans>Offers accepted</Trans>:
                                </span>
                                <span>{closedChangeRequests}</span>
                            </div>
                        </div>
                    )}

                    <div className={classnames(theme.deadlineHeader, theme.deadlinesContainer)}>
                        <BuyerDashboardDeadlines />
                    </div>
                </div>
            </div>
        </>
    );
};

export default BuyerDashboard;
